import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { updateSettings } from '../../../base/settings/actions';
import Checkbox from '../../../base/ui/components/web/Checkbox';
import Select from '../../../base/ui/components/web/Select';
import { setRequestingSubtitles } from '../../actions.any';
/**
 * Value for the “original language” option in the translate-to select.
 */
const ORIGINAL_LANGUAGE_OPTION = 'transcribing.original';
/**
 * The styles for the LanguageSelector component.
 *
 * @param {Theme} theme - The MUI theme.
 * @returns {Object} The styles object.
 */
const useStyles = makeStyles()(theme => {
    return {
        container: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: theme.spacing(2),
            gap: theme.spacing(2)
        },
        languageGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            flex: '1 1 auto',
            minWidth: 0
        },
        select: {
            flex: 1,
            minWidth: 120,
            maxWidth: 220
        },
        label: {
            ...theme.typography.bodyShortRegular,
            color: theme.palette.languageSelectorText,
            whiteSpace: 'nowrap'
        },
        stageToggle: {
            flex: '0 0 auto',
            margin: 0,
            whiteSpace: 'nowrap'
        }
    };
});
/**
 * Component that renders a language selection dropdown (original only for now)
 * and the “show subtitles on stage” toggle next to it.
 *
 * @returns {JSX.Element} - The rendered component.
 */
function LanguageSelector() {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const selectedLanguage = useSelector((state) => state['features/subtitles']._language);
    const requestingSubtitles = useSelector((state) => state['features/subtitles']._requestingSubtitles);
    const showSubtitlesOnStage = useSelector((state) => Boolean(state['features/base/settings'].showSubtitlesOnStage));
    const isAsyncTranscriptionEnabled = useSelector((state) => state['features/base/conference'].conference?.getMetadataHandler()?.getMetadata()?.asyncTranscription);
    /**
     * Only “original” is offered until machine translation is available.
     * Clear any previously selected translation language so on-stage captions
     * receive transcription (source) results in real time.
     */
    useEffect(() => {
        if (requestingSubtitles && selectedLanguage) {
            dispatch(setRequestingSubtitles(true, showSubtitlesOnStage, null));
        }
    }, [dispatch, requestingSubtitles, selectedLanguage, showSubtitlesOnStage]);
    /**
     * Only “original” is offered until machine translation is available.
     */
    const languages = [
        {
            value: ORIGINAL_LANGUAGE_OPTION,
            label: t(ORIGINAL_LANGUAGE_OPTION)
        }
    ];
    /**
     * Handles language selection changes.
     *
     * @param {ChangeEvent<HTMLSelectElement>} e - The change event.
     * @returns {void}
     */
    const onLanguageChange = useCallback((e) => {
        let { value } = e.target;
        if (value === ORIGINAL_LANGUAGE_OPTION) {
            value = null;
        }
        dispatch(setRequestingSubtitles(true, showSubtitlesOnStage, value));
    }, [dispatch, showSubtitlesOnStage]);
    /**
     * Toggles on-stage (main video) subtitles without closing the sidebar history.
     *
     * @param {ChangeEvent<HTMLInputElement>} e - The change event.
     * @returns {void}
     */
    const onShowSubtitlesOnStageChanged = useCallback((e) => {
        const { checked } = e.target;
        dispatch(updateSettings({ showSubtitlesOnStage: checked }));
        dispatch(setRequestingSubtitles(true, checked, null));
    }, [dispatch]);
    if (isAsyncTranscriptionEnabled) {
        return (React.createElement("div", { className: classes.container },
            React.createElement(Checkbox, { checked: showSubtitlesOnStage, className: classes.stageToggle, label: t('settings.showSubtitlesOnStage'), name: 'show-subtitles-on-stage', onChange: onShowSubtitlesOnStageChanged })));
    }
    return (React.createElement("div", { className: classes.container },
        React.createElement("div", { className: classes.languageGroup },
            React.createElement("span", { className: classes.label },
                t('transcribing.translateTo'),
                ":"),
            React.createElement(Select, { className: classes.select, id: 'subtitles-language-select', onChange: onLanguageChange, options: languages, value: ORIGINAL_LANGUAGE_OPTION })),
        React.createElement(Checkbox, { checked: showSubtitlesOnStage, className: classes.stageToggle, label: t('settings.showSubtitlesOnStage'), name: 'show-subtitles-on-stage', onChange: onShowSubtitlesOnStageChanged })));
}
export default LanguageSelector;
